import { EnvironmentProviders, inject } from '@angular/core';
import { TrackWorkflowService, provideWorkflowTracking } from '@anduin/shared';
import {
  COLLECTIONS_STEP_TO_EVENT_TYPE,
  COLLECTIONS_STEP_TO_WORKFLOW,
  CollectionsWorkflowStep,
  CollectionsWorkflowStepMetaDataMap,
} from './collections-workflow.models';

/**
 * Strongly-typed `TrackWorkflowService` view for the collections module. `trackWorkflow`
 * narrows `metadata` to the shape declared on `CollectionsWorkflowStepMetaDataMap` for
 * whatever step the caller passes.
 */
export type CollectionsTracker = TrackWorkflowService<CollectionsWorkflowStep, CollectionsWorkflowStepMetaDataMap>;

/**
 * Inject the workflow tracker narrowed to the collections taxonomy. Use this in place
 * of `inject(TrackWorkflowService)` anywhere in the collections module that needs
 * `trackWorkflow` — direct injection of `TrackWorkflowService` is blocked by
 * `no-restricted-imports` (see `eslint.config.js`).
 */
export function injectCollectionsTracking(): CollectionsTracker {
  return inject(TrackWorkflowService) as CollectionsTracker;
}

/**
 * Register the collections module's workflow taxonomy. Call once at app providers so
 * `trackWorkflow(<collections step>)` resolves at runtime against the shared
 * `TrackWorkflowService` regardless of which lazy module emits the event.
 */
export function provideCollectionsWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<CollectionsWorkflowStep, CollectionsWorkflowStepMetaDataMap>({
    stepToWorkflow: COLLECTIONS_STEP_TO_WORKFLOW,
    stepToEventType: COLLECTIONS_STEP_TO_EVENT_TYPE,
  });
}
